/** How much of a calendar date is known. */
export type DatePrecision = "year" | "month" | "day";

/**
 * A calendar date known only to a certain precision: `value` is an ISO 8601 string truncated to
 * it (`2024`, `2024-04`, `2024-04-15`). Mirrors tangrowth's `PartialDate`.
 */
export interface PartialDate {
  value: string;
  precision: DatePrecision;
}

const PARTIAL_ISO_DATE = /^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/;

/** Parses an ISO 8601 year, year-month or full date; null when absent or not a real calendar date. */
export function parsePartialDate(raw: unknown): PartialDate | null {
  if (typeof raw !== "string") return null;
  const match = PARTIAL_ISO_DATE.exec(raw.trim());
  if (!match) return null;
  const [value, , month, day] = match;
  const precision: DatePrecision = day ? "day" : month ? "month" : "year";
  const earliest = earliestIsoDate({ value, precision });
  // Date rolls Feb 30 over to Mar 2 and rejects month 13, so only a round-trip match is real.
  const parsed = new Date(`${earliest}T00:00:00Z`);
  const isRealDate =
    !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === earliest;
  return isRealDate ? { value, precision } : null;
}

/** The first day the date covers (`2024-04` → `2024-04-01`), for typed DATE columns; null in, null out. */
export function earliestIsoDate(date: PartialDate | null): string | null {
  if (date === null) return null;
  return date.precision === "year"
    ? `${date.value}-01-01`
    : date.precision === "month"
      ? `${date.value}-01`
      : date.value;
}

/** Parses an ISO partial date straight to its first day (`"2024-04"` → `"2024-04-01"`); null when unparseable. */
export function parseEarliestIsoDate(raw: unknown): string | null {
  return earliestIsoDate(parsePartialDate(raw));
}
